import type { HintStrategy, LangCode, Meeting } from "@scoach/types";
import { LANGUAGES, langMeta } from "@scoach/types";
import { Bolt, Chev, Globe, Mic, MicOff, Play, Stop } from "@scoach/ui/icons";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { ThemeToggle } from "../../../../components/ThemeToggle.tsx";
import { api } from "../../../../lib/http.ts";
import { authApi } from "../../../auth/api.ts";
import { useAuthStore } from "../../../auth/store.ts";
import { useConfirmLeaveLiveMeeting } from "../hooks/useConfirmLeaveLiveMeeting.ts";
import { useLiveMeetingStore } from "../store.ts";

const HINT_STRATEGY_LABELS: Record<HintStrategy, string> = {
  live: "Live hints",
  balanced: "Balanced hints",
  focused: "Focused hints",
};

export interface AppHeaderProps {
  meeting: Meeting;
  onToggleListening: () => void;
  onEnd: () => void;
  isSimulation?: boolean;
  simulationConnected?: boolean;
  simulationError?: string | null;
}

function elapsed(startedAt: number | null) {
  if (!startedAt) return "00:00";
  const sec = Math.floor((Date.now() - startedAt) / 1000);
  const m = Math.floor(sec / 60)
    .toString()
    .padStart(2, "0");
  const s = (sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export function AppHeader({
  meeting,
  onToggleListening,
  onEnd,
  isSimulation,
  simulationConnected,
  simulationError,
}: AppHeaderProps) {
  const { listening, muted, latencyMs, startedAt, setMuted } = useLiveMeetingStore();
  const confirmLeave = useConfirmLeaveLiveMeeting();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const hintStrategy: HintStrategy = user?.settings.hintStrategy ?? "live";

  // On-screen assistance language (hints/answers), independent of the spoken
  // meeting language. Changeable live; persisted to the meeting + running session.
  const [assistLang, setAssistLang] = useState<"auto" | LangCode>(
    meeting.assistanceLanguage ?? "auto",
  );

  function changeAssistanceLanguage(next: "auto" | LangCode) {
    setAssistLang(next);
    // Update the running session/meeting AND remember it as the user's default so
    // the next meeting starts in this language.
    void api(`/meetings/${meeting.id}/assistance-language`, {
      method: "PATCH",
      body: { language: next },
    }).catch(() => {});
    if (user) {
      setUser({ ...user, settings: { ...user.settings, assistanceLanguage: next } });
    }
    void authApi.updateSettings({ assistanceLanguage: next }).catch(() => {});
  }

  // Spoken/meeting language (what people talk in) — pins STT + generated content
  // for the meeting. Changeable live; persisted to the meeting + remembered as the
  // user's default so the next meeting starts in this language.
  const [spokenLang, setSpokenLang] = useState<"auto" | LangCode>(meeting.language ?? "auto");

  function changeSpokenLanguage(next: "auto" | LangCode) {
    setSpokenLang(next);
    void api(`/meetings/${meeting.id}/spoken-language`, {
      method: "PATCH",
      body: { language: next },
    }).catch(() => {});
    if (user) {
      setUser({ ...user, settings: { ...user.settings, spokenLanguage: next } });
    }
    void authApi.updateSettings({ spokenLanguage: next }).catch(() => {});
  }

  function changeHintStrategy(next: HintStrategy) {
    // Optimistically update the store so the header reflects the choice and it
    // becomes the user's default. Persist to the user doc + the running session.
    if (user) {
      setUser({ ...user, settings: { ...user.settings, hintStrategy: next } });
    }
    void authApi.updateSettings({ hintStrategy: next }).catch(() => {});
    void api(`/meetings/${meeting.id}/hint-strategy`, {
      method: "PATCH",
      body: { strategy: next },
    }).catch(() => {});
  }

  return (
    <header className="topbar">
      <div className="topbar-left">
        <Link to="/dashboard" className="brand brand-link" onClick={confirmLeave} title="SellSynq - Your Invisible AI Sales Assistant">
          <img src="/supercloud-mark.svg" alt="SellSynq" width={32} height={32} />
          <div className="brand-text">
            <div className="brand-name">SellSynq</div>
            <div className="brand-sub">
              Your Invisible AI Sales Assistant
            </div>
          </div>
        </Link>

        <div className="meeting-chip">
          <div className="meeting-chip-dot" />
          <div>
            <div className="meeting-chip-title">
              {meeting.account.name}
              {meeting.account.deal ? ` · ${meeting.account.deal}` : ""}
              {meeting.role ? (
                <span className="brand-tag" style={{ marginLeft: 6 }}>
                  {meeting.role}
                </span>
              ) : null}
            </div>
            <div className="meeting-chip-sub">
              {meeting.account.industry ?? meeting.title}
              {meeting.account.region ? ` · ${meeting.account.region}` : ""}
            </div>
          </div>
          <Link
            to="/meetings/$id/summary"
            params={{ id: meeting.id }}
            className="meeting-chip-edit"
            title="View this meeting's summary"
            onClick={confirmLeave}
          >
            <Chev />
          </Link>
        </div>
      </div>

      <div className="topbar-center">
        <div className="status-bar">
          <span className={`live-dot${isSimulation ? " sim-dot" : ""}`} />
          <span className="status-bar-label">
            {isSimulation
              ? simulationConnected
                ? "SIMULATION"
                : "SIM · CONNECTING"
              : listening
                ? "LIVE"
                : "PAUSED"}
          </span>
          <span className="status-bar-time">{elapsed(startedAt)}</span>
          <span className="status-bar-sep" />
          <span className="status-bar-meta">
            <Globe size={14} />{" "}
            {meeting.language && meeting.language !== "auto" && meeting.language !== "en"
              ? `${langMeta(meeting.language).labelEn} ↔ English`
              : meeting.language === "en"
                ? "English"
                : "Auto-detect"}
          </span>
          {latencyMs != null && (
            <>
              <span className="status-bar-sep" />
              <span className="status-bar-meta">
                <Bolt size={14} /> {latencyMs}ms
              </span>
            </>
          )}
        </div>
      </div>

      <div className="topbar-right">
        <ThemeToggle />

        <select
          className="strategy-select"
          value={hintStrategy}
          onChange={(e) => changeHintStrategy(e.target.value as HintStrategy)}
          title="How often SellSynq surfaces hints. Live = frequent; Balanced = only when useful; Focused = only critical moments."
        >
          {(["live", "balanced", "focused"] as HintStrategy[]).map((s) => (
            <option key={s} value={s}>
              {HINT_STRATEGY_LABELS[s]}
            </option>
          ))}
        </select>

        <select
          className="strategy-select"
          value={spokenLang}
          onChange={(e) => changeSpokenLanguage(e.target.value as "auto" | LangCode)}
          title="The spoken meeting language. Auto-detect picks it up; choosing one pins it so transcription stays stable. Remembered for next time."
        >
          <option value="auto">Meeting: Auto-detect</option>
          {LANGUAGES.map((l) => (
            <option key={l.code} value={l.code}>
              Meeting: {l.labelEn}
            </option>
          ))}
        </select>

        <select
          className="strategy-select"
          value={assistLang}
          onChange={(e) => changeAssistanceLanguage(e.target.value as "auto" | LangCode)}
          title="Language of the hints & answers SellSynq shows you — independent of the spoken meeting language."
        >
          <option value="auto">Hints: same as meeting</option>
          {LANGUAGES.map((l) => (
            <option key={l.code} value={l.code}>
              Hints: {l.labelEn}
            </option>
          ))}
        </select>

        <button
          type="button"
          className={`pill-btn ${muted ? "pill-muted" : ""}`}
          onClick={() => setMuted(!muted)}
        >
          {muted ? <MicOff size={16} /> : <Mic size={16} />}
          <span>{muted ? "Listening muted" : "Mute listening"}</span>
        </button>

        {simulationError && (
          <span style={{ fontSize: 12, color: "var(--gc-red, #d93025)" }}>{simulationError}</span>
        )}

        <button
          type="button"
          className={`pill-btn primary ${listening || simulationConnected ? "recording" : ""}`}
          onClick={listening || simulationConnected ? onEnd : onToggleListening}
        >
          {listening || simulationConnected ? <Stop size={14} /> : <Play size={14} />}
          <span>
            {isSimulation
              ? simulationConnected
                ? "End simulation"
                : "Start simulation"
              : listening
                ? "End meeting"
                : "Start meeting"}
          </span>
        </button>
      </div>
    </header>
  );
}
